import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Globe } from 'lucide-react';
import { LOGO_DARK } from '../App';
import { useTranslation } from '../context/LanguageContext';
import { LANGUAGES, useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config/api';

// Default content if DB is empty
const DEFAULT_PRIVACY = `
<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">1. Introduzione</h2>
  <p class="text-gray-700 leading-relaxed">
    Benvenuto su Slimmy.com ("noi", "nostro" o "Servizio"). La tua privacy è importante per noi. 
    Questa Informativa sulla Privacy spiega come raccogliamo, utilizziamo, divulghiamo e proteggiamo 
    le tue informazioni quando utilizzi la nostra applicazione mobile e i servizi correlati.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">2. Informazioni che Raccogliamo</h2>
  <p class="text-gray-700 leading-relaxed mb-3">Raccogliamo i seguenti tipi di informazioni:</p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li><strong>Informazioni dell'Account:</strong> Nome, indirizzo email e foto del profilo quando accedi tramite Google.</li>
    <li><strong>Dati sulla Salute:</strong> Peso, altezza, obiettivi di peso e progressi che inserisci volontariamente.</li>
    <li><strong>Dati di Attività:</strong> Informazioni sulle camminate tracciate tramite GPS (opzionale).</li>
    <li><strong>Contenuti Generati:</strong> Post, commenti e ricette che condividi sulla piattaforma.</li>
    <li><strong>Dati Tecnici:</strong> Tipo di dispositivo, sistema operativo e informazioni di utilizzo dell'app.</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">3. Come Utilizziamo le Tue Informazioni</h2>
  <p class="text-gray-700 leading-relaxed mb-3">Utilizziamo le informazioni raccolte per:</p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li>Fornire e mantenere il nostro Servizio</li>
    <li>Personalizzare la tua esperienza e i piani alimentari</li>
    <li>Calcolare progressi e statistiche</li>
    <li>Abilitare le funzionalità social (sfide, feed)</li>
    <li>Inviare notifiche relative al Servizio</li>
    <li>Migliorare e ottimizzare l'app</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">4. Condivisione delle Informazioni</h2>
  <p class="text-gray-700 leading-relaxed mb-3">
    <strong>Non vendiamo</strong> le tue informazioni personali. Potremmo condividere informazioni con:
  </p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li><strong>Altri Utenti:</strong> Le informazioni che scegli di condividere pubblicamente (post, sfide)</li>
    <li><strong>Fornitori di Servizi:</strong> Terze parti che ci aiutano a gestire il Servizio (hosting, analytics)</li>
    <li><strong>Requisiti Legali:</strong> Se richiesto dalla legge o per proteggere i nostri diritti</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">5. Sicurezza dei Dati</h2>
  <p class="text-gray-700 leading-relaxed">
    Utilizziamo misure di sicurezza tecniche e organizzative appropriate per proteggere le tue 
    informazioni personali. Tuttavia, nessun sistema è completamente sicuro e non possiamo garantire 
    la sicurezza assoluta dei tuoi dati.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">6. I Tuoi Diritti</h2>
  <p class="text-gray-700 leading-relaxed mb-3">Hai il diritto di:</p>
  <ul class="list-disc pl-6 text-gray-700 space-y-2">
    <li>Accedere ai tuoi dati personali</li>
    <li>Correggere dati inesatti</li>
    <li>Richiedere la cancellazione dei tuoi dati</li>
    <li>Opporti al trattamento dei tuoi dati</li>
    <li>Richiedere la portabilità dei dati</li>
  </ul>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">7. Cookie e Tecnologie Simili</h2>
  <p class="text-gray-700 leading-relaxed">
    Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza, analizzare l'utilizzo 
    del Servizio e personalizzare i contenuti.
  </p>
</section>

<section>
  <h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contatti</h2>
  <p class="text-gray-700 leading-relaxed">
    Per domande sulla privacy: <strong>privacy@slimmy.app</strong>
  </p>
</section>
`;

export default function Privacy() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [content, setContent] = useState(null);
  const [updated, setUpdated] = useState('Marzo 2026');
  const [loading, setLoading] = useState(true);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const loadPrivacy = async () => {
      try {
        const res = await fetch(`${API_URL}/api/legal/privacy?lang=${language}`);
        const data = await res.json();
        if (data.content) {
          setContent(data.content);
          if (data.updated) setUpdated(data.updated);
        } else {
          setContent(DEFAULT_PRIVACY);
        }
      } catch (err) {
        console.error('Error loading privacy:', err);
        setContent(DEFAULT_PRIVACY);
      } finally {
        setLoading(false);
      }
    };
    loadPrivacy();
  }, [language]);

  return (
    <div className="fixed inset-0 bg-white overflow-y-auto">
      {/* Header with safe area */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="privacy-back-btn"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <img src={LOGO_DARK} alt="Slimmy" className="h-8" />
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="privacy-lang-btn"
              aria-label={t('legal.language') || 'Language'}
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>
            {langOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-20 cursor-default"
                  onClick={() => setLangOpen(false)}
                  aria-label="close"
                  tabIndex={-1}
                />
                <div
                  className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-30"
                  data-testid="privacy-lang-menu"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                        l.code === language ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-700'
                      }`}
                      data-testid={`privacy-lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content — backend HTML already includes its own H1 + last updated */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div 
            className="prose prose-gray max-w-none space-y-6"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}

        <div className="mt-12 pt-6 pb-12 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            © 2026 Slimmy.com - {t('legal.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
}
